/**
 * The static world: terrain, river surface, shoreline foam, vegetation and rocks (GDD §3.3–3.5).
 * Built in the concept's order (concept L703–L762) so the world-generation stream is consumed identically.
 */
import {
  Color,
  DoubleSide,
  Mesh,
  MeshBasicMaterial,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  Scene,
  Vector2,
  type BufferGeometry,
  type InstancedMesh,
  type Texture,
} from 'three';
import type { Rng } from '../core/rng.ts';
import type { Look } from './looks.ts';
import type { Mode } from './modes.ts';
import type { WorldProfile } from './profiles.ts';
import { buildTerrain, buildTerrainChunks } from './terrain.ts';
import { applyConceptLand, surfaceHeight } from './terrainSurface.ts';
import { LAYER, type WorldSettings } from './fidelity.ts';
import { ISLAND_Z } from './profiles.ts';
import { PERIOD } from '../core/math.ts';
import type { TextureSet } from './textures.ts';
import { bandedVegetationMeshes, vegetationLayers, vegetationMaterials, vegetationMeshes } from './vegetation.ts';
import { foamGeometry, islandFoamGeometry, N2_REPEAT, waterGeometry, WORLD_Z0, worldZ1 } from './water.ts';

export interface StaticWorld {
  /** One mesh (concept) or z-chunks that culling can skip (GDD). */
  terrain: Mesh[];
  water: Mesh<ReturnType<typeof waterGeometry>, MeshPhysicalMaterial>;
  foam: Mesh[];
  vegetation: InstancedMesh[];
  /**
   * The island foam's own copy of the foam texture (same image): the bank foam's texture carries the world-shift
   * phase along z, while island foam UVs run around each island and must not move with it.
   */
  islandFoamMap: Texture | null;
  /** Terrain height as drawn, for bank collisions (GDD §4.2) and tests. */
  surface(x: number, z: number): number;
  z0: number;
  z1: number;
}

export function buildStaticWorld(
  scene: Scene,
  W: WorldProfile,
  mode: Mode,
  look: Look,
  TX: TextureSet,
  rand: Rng,
  settings: WorldSettings,
): StaticWorld {
  const z0 = WORLD_Z0;
  const z1 = worldZ1(mode);

  TX.ground.repeat.set(1, 1);
  const tMat = new MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.93,
    metalness: 0,
    map: TX.ground,
    bumpMap: TX.ground,
    bumpScale: 1.4,
    envMapIntensity: 0.35,
  });
  const { nx, nz, chunkLength, lattice } = settings.terrain;
  const tz1 = settings.terrain.z1;
  const finish = lattice ? (g: BufferGeometry) => applyConceptLand(g, W, lattice, tz1) : undefined;
  const geos = chunkLength
    ? buildTerrainChunks(W, z0, tz1, nx, nz, 320, chunkLength, finish)
    : [buildTerrain(W, z0, tz1, nx, nz, 320)];
  const surface = lattice ? (x: number, z: number) => surfaceHeight(W, lattice, x, z) : W.height;
  const terrain = geos.map((g) => {
    const m = new Mesh(g, tMat);
    m.receiveShadow = true;
    m.castShadow = true;
    scene.add(m);
    return m;
  });

  TX.n1.repeat.set(1, 1);
  TX.n2.repeat.set(N2_REPEAT, N2_REPEAT);
  const wMat = new MeshPhysicalMaterial({
    color: 0xffffff,
    vertexColors: true,
    roughness: 0.16,
    metalness: 0.02,
    normalMap: TX.n1,
    normalScale: new Vector2(0.45, 0.45),
    clearcoat: 1,
    clearcoatRoughness: 0.04,
    clearcoatNormalMap: TX.n2,
    clearcoatNormalScale: new Vector2(0.3, 0.3),
    envMapIntensity: look.env,
  });
  const water = new Mesh(waterGeometry(W, look, z0, settings.water.z1, settings.water.nz), wMat);
  water.receiveShadow = true;
  scene.add(water);

  const foamMat = new MeshBasicMaterial({
    map: TX.foam,
    transparent: true,
    depthWrite: false,
    color: new Color(look.foam),
    opacity: 0.55,
    side: DoubleSide,
  });
  const foam = ([-1, 1] as const).map((s) => {
    const m = new Mesh(foamGeometry(W, s, z0, settings.foam.z1, settings.foam.nz), foamMat);
    if (settings.stableLayers) m.renderOrder = LAYER.foam;
    scene.add(m);
    return m;
  });
  let islandFoamMap: Texture | null = null;
  if (settings.islandFoam && W.island) {
    islandFoamMap = TX.foam.clone();
    const islandFoamMat = foamMat.clone();
    islandFoamMat.map = islandFoamMap;
    for (let iz = ISLAND_Z + PERIOD; iz > z1; iz -= PERIOD) {
      if (iz > z0) continue;
      const m = new Mesh(islandFoamGeometry(W, iz), islandFoamMat);
      m.renderOrder = LAYER.foam; // island foam exists only with the GDD settings
      scene.add(m);
      foam.push(m);
    }
  }

  const layers = vegetationLayers(W, rand, mode.world === 'canyon', z0, z1);
  const vegMats = vegetationMaterials();
  const vegetation = settings.vegetationBand
    ? bandedVegetationMeshes(layers, vegMats, settings.vegetationBand)
    : vegetationMeshes(layers, vegMats);
  for (const m of vegetation) scene.add(m);

  return { terrain, water, foam, vegetation, islandFoamMap, surface, z0, z1 };
}
